// Static portion of the JVVNL search index: pages, metrics, and weather
// parameters. Routes here are verified against src/Routes.tsx (JvvnlRoutes).
// Dynamic entities (feeders / plants / substations) are merged in at runtime.

import { SearchItem } from "./types";

const GEN_LOAD_BALANCE = "jvvnl/jvvnl-generation-load-balance";
const METRIC_PAGE = "jvvnl/jvvnl-metric";
const LOAD_MAP_PAGE = "jvvnl/load-map";
const FORECAST_PAGE = "jvvnl/forecast";

export const STATIC_PAGES: SearchItem[] = [
  {
    id: "page-overview",
    type: "page",
    label: "Net Load & DRE Flow (Overview)",
    searchText: "net load dre flow overview demand generation feeder",
    page: GEN_LOAD_BALANCE,
    category: "Page",
    priority: 10,
    hint: "Page",
  },
  {
    id: "page-metric",
    type: "page",
    label: "Net Load & DRE Flow (Metric)",
    searchText: "net load dre flow metric overview consumption",
    page: METRIC_PAGE,
    category: "Page",
    priority: 10,
    hint: "Page",
  },
  {
    id: "page-load-map",
    type: "page",
    label: "Load Map",
    searchText: "load map geography location",
    page: LOAD_MAP_PAGE,
    category: "Page",
    priority: 10,
    hint: "Page",
  },
  {
    id: "page-forecast",
    type: "page",
    label: "Load & Generation Forecast",
    searchText: "load generation forecast prediction adjustment",
    page: FORECAST_PAGE,
    category: "Page",
    priority: 10,
    hint: "Page",
  },
];

// Metrics & weather parameters: navigate to the relevant page (no deep-select).
export const STATIC_METRICS: SearchItem[] = [
  {
    id: "metric-peak-load",
    type: "metric",
    label: "Peak Load",
    searchText: "peak load maximum demand",
    page: METRIC_PAGE,
    category: "Metric",
    priority: 5,
    hint: "Metric",
  },
  {
    id: "metric-net-consumption",
    type: "metric",
    label: "Net Consumption",
    searchText: "net consumption total demand",
    page: METRIC_PAGE,
    category: "Metric",
    priority: 5,
    hint: "Metric",
  },
  {
    id: "metric-generation",
    type: "metric",
    label: "Generation",
    searchText: "generation output supply solar wind",
    page: GEN_LOAD_BALANCE,
    category: "Metric",
    priority: 5,
    hint: "Metric",
  },
  {
    id: "metric-net-consumption-flow",
    type: "metric",
    label: "Net Consumption Flow",
    searchText: "net consumption flow dre overview chart default",
    page: GEN_LOAD_BALANCE,
    category: "Metric",
    priority: 4,
    hint: "Metric · Overview",
  },
  {
    id: "metric-net-load",
    type: "metric",
    label: "Net Load",
    searchText: "net load demand minus generation residual",
    page: METRIC_PAGE,
    category: "Metric",
    priority: 5,
    hint: "Metric",
  },
  {
    id: "metric-demand",
    type: "metric",
    label: "Demand",
    searchText: "demand load average peak total",
    page: METRIC_PAGE,
    category: "Metric",
    priority: 5,
    hint: "Metric",
  },
  {
    id: "metric-total-energy",
    type: "metric",
    label: "Total Energy",
    searchText: "total energy mwh units consumption",
    page: METRIC_PAGE,
    category: "Metric",
    priority: 5,
    hint: "Metric",
  },
  {
    id: "metric-forecast",
    type: "metric",
    label: "Forecast",
    searchText: "forecast predicted demand generation net projection",
    page: METRIC_PAGE,
    directQuery: "tab=forecast",
    category: "Metric",
    priority: 5,
    hint: "Metric · Forecast tab",
  },
  {
    id: "metric-data-quality",
    type: "metric",
    label: "Data Quality",
    searchText: "data quality availability nulls zeros completeness missing",
    page: METRIC_PAGE,
    category: "Metric",
    priority: 4,
    hint: "Metric",
  },
  {
    id: "metric-top-feeders",
    type: "metric",
    label: "Top Feeders by Load",
    searchText: "top feeders load contribution ranking highest",
    page: METRIC_PAGE,
    directQuery: "granularity=Feeder&rank=top&hl=fq-high",
    category: "Ranking",
    priority: 3,
    hint: "Ranking · Top",
  },
  {
    id: "metric-bottom-feeders",
    type: "metric",
    label: "Bottom Feeders by Load",
    searchText: "bottom feeders load contribution ranking lowest",
    page: METRIC_PAGE,
    directQuery: "granularity=Feeder&rank=bottom&hl=fq-low",
    category: "Ranking",
    priority: 3,
    hint: "Ranking · Bottom",
  },
  // Load Contribution rankings exist at Circle / Transco / Discom Substation
  // granularity (the Load rank table; at Feeder granularity the page shows the
  // Data Quality tables instead — see the Feeder ranking entries above). One Top
  // + one Bottom per granularity, each forcing its granularity, flipping the Load
  // rank table to the right view, and highlighting it. "Discom Substation" is
  // URL-encoded (space → %20).
  ...(
    [
      ["Circle", "Circle", "circle"],
      ["Transco", "Transco", "transco"],
      ["Discom Substation", "Discom%20Substation", "discom"],
    ] as const
  ).flatMap<SearchItem>(([gLabel, gValue, gKey]) =>
    (["top", "bottom"] as const).map<SearchItem>((dir) => ({
      id: `metric-${dir}-load-${gKey}`,
      label: `${dir === "top" ? "Top" : "Bottom"} Load Contribution (${gLabel})`,
      type: "metric",
      searchText: `${dir} load contribution demand ranking ${
        dir === "top" ? "highest most peak" : "lowest least"
      } ${gLabel.toLowerCase()}`,
      page: METRIC_PAGE,
      directQuery: `granularity=${gValue}&rank=${dir}&hl=load-${dir}`,
      category: "Ranking",
      priority: 3,
      hint: `Ranking · Load · ${gLabel}`,
    })),
  ),

  // Generator rankings exist at Circle / Transco / Discom Substation granularity
  // (no gen table at Feeder). One Top + one Bottom result per granularity, each
  // forcing its granularity, flipping the DRE Generators table to the right view,
  // and highlighting it. "Discom Substation" is URL-encoded (space → %20).
  ...(
    [
      ["Circle", "Circle", "circle"],
      ["Transco", "Transco", "transco"],
      ["Discom Substation", "Discom%20Substation", "discom"],
    ] as const
  ).flatMap<SearchItem>(([gLabel, gValue, gKey]) =>
    (["top", "bottom"] as const).map<SearchItem>((dir) => ({
      id: `metric-${dir}-generators-${gKey}`,
      label: `${dir === "top" ? "Top" : "Bottom"} Generators (${gLabel})`,
      type: "metric",
      searchText: `${dir} generators plants generation contribution ranking ${
        dir === "top" ? "highest most" : "lowest least"
      } ${gLabel.toLowerCase()}`,
      page: METRIC_PAGE,
      directQuery: `granularity=${gValue}&genrank=${dir}&hl=gen`,
      category: "Ranking",
      priority: 3,
      hint: `Ranking · Generators · ${gLabel}`,
    })),
  ),
  // [label, searchText, WEATHER_PARAMS key]. The param is attached at build time
  // so clicking a weather metric ALWAYS carries it to the Chart page (enables the
  // Weather variable + that series), independent of whether the typed query
  // happened to contain a weather token.
  ...[
    ["Temperature", "temperature heat thermal", "temperature_2m"],
    ["Humidity", "humidity moisture", "relative_humidity_2m"],
    ["Precipitation", "precipitation rain", "precipitation"],
    ["Cloud Cover", "cloud cover", "cloud_cover"],
    ["Wind Speed", "wind speed breeze", "wind_speed_10m"],
    ["Sunshine Duration", "sunshine duration solar", "sunshine_duration"],
    ["Rain", "rain rainfall precipitation", "rain"],
    ["Direct Radiation", "direct radiation instant irradiance solar", "direct_radiation_instant"],
  ].map<SearchItem>(([label, searchText, weatherParam]) => ({
    id: `metric-weather-${label.toLowerCase().replace(/\s+/g, "-")}`,
    type: "metric",
    label,
    searchText: `${searchText} weather`,
    page: GEN_LOAD_BALANCE,
    category: "Weather",
    priority: 3,
    hint: "Weather",
    weatherParam,
  })),

  // ── Metric-card results ────────────────────────────────────────────────────
  // The summary cards across the top of the Metric page, as searchable results.
  // Each forces its granularity + Actual/Forecast tab and pulses the exact card
  // (hl=card-*). Card availability per the page's render rules:
  //   • Load, Energy           → all granularities, both tabs
  //   • Generation, Net Load   → Circle/Transco/Discom only (filtered at Feeder)
  //   • Morning/Solar/Evening Peak → all granularities, both tabs
  //   • Data Summary           → Feeder, Actual only
  ...(() => {
    const GRANS: [string, string, string][] = [
      ["Circle", "Circle", "circle"],
      ["Transco", "Transco", "transco"],
      ["Discom Substation", "Discom%20Substation", "discom"],
      ["Feeder", "Feeder", "feeder"],
    ];
    const TABS: [string, string][] = [
      ["Actual", "actual"],
      ["Forecast", "forecast"],
    ];
    // [label, hl key, search keywords, predicate(granularityValue, tabValue)]
    const CARDS: [
      string,
      string,
      string,
      (g: string, t: string) => boolean,
    ][] = [
      ["Load", "card-load", "load peak average demand mw", () => true],
      ["Energy", "card-energy", "energy total mwh units consumption", () => true],
      [
        "Generation",
        "card-generation",
        "generation output supply solar wind mw",
        (g) => g !== "Feeder",
      ],
      [
        "Net Load",
        "card-netload",
        "net load residual demand minus generation mw",
        (g) => g !== "Feeder",
      ],
      [
        "Morning Peak Load",
        "card-morning",
        "morning peak load early",
        () => true,
      ],
      ["Solar Peak Load", "card-solar", "solar peak load midday", () => true],
      [
        "Evening Peak Load",
        "card-evening",
        "evening peak load night",
        () => true,
      ],
      [
        "Data Summary",
        "card-datasummary",
        "data summary availability zeros missing quality completeness",
        (g, t) => g === "Feeder" && t === "actual",
      ],
    ];
    const out: SearchItem[] = [];
    for (const [gLabel, gValue, gKey] of GRANS) {
      for (const [tLabel, tValue] of TABS) {
        for (const [cLabel, hlKey, kw, ok] of CARDS) {
          if (!ok(gValue, tValue)) continue;
          out.push({
            id: `metric-card-${hlKey}-${gKey}-${tValue}`,
            label: `${cLabel} (${gLabel} · ${tLabel})`,
            type: "metric",
            searchText: `${cLabel} ${kw} ${gLabel} ${tLabel}`.toLowerCase(),
            page: METRIC_PAGE,
            directQuery: `granularity=${gValue}&tab=${tValue}&hl=${hlKey}`,
            category: "Metric",
            priority: 2,
            hint: `Metric · ${gLabel} · ${tLabel}`,
          });
        }
      }
    }
    return out;
  })(),
];

export const STATIC_INDEX: SearchItem[] = [...STATIC_PAGES, ...STATIC_METRICS];
